import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import LandingPage from "@/pages/LandingPage";
import CustomerSelectPage from "@/pages/public/CustomerSelectPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import CustomerLogin from "@/pages/CustomerLogin";
import CustomerSignup from "@/pages/CustomerSignup";
import CustomerMyPage from "@/pages/CustomerMyPage";
import PortfolioDetailPage from "@/pages/portfolio/PortfolioDetailPage";
import PortfolioHubPage from "@/pages/portfolio/PortfolioHubPage";
import PortfolioDemoPage from "@/pages/portfolio/PortfolioDemoPage";
import AboutPage2 from "@/pages/AboutPage2";
import GuidesHubPage from "@/pages/search/GuidesHubPage";
import GuideDetailPage from "@/pages/search/GuideDetailPage";
import ServicesIndexPage from "@/pages/search/ServicesIndexPage";
import ServiceLandingPage from "@/pages/search/ServiceLandingPage";
import CapabilitiesPage from "@/pages/search/CapabilitiesPage";
import { DESIGN_PREVIEW_ENABLED } from "@/pages/designPreview/designPreviewEnabled";
import ScrollProgressGate from "@/components/ScrollProgressGate";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CmsProvider } from "@/lib/cms";
import { SiteKnowledgeProvider } from "@/lib/siteKnowledge";
import { ThemeProvider } from "@/lib/theme";
import ContactInquiryProvider from "@/components/contact/ContactInquiryProvider";
import ErrorBoundary from "@/components/ErrorBoundary";
import SeoDocumentHead from "@/components/SeoDocumentHead";
import { CapabilitiesReturnTracker } from "@/lib/knowledgeReturn";

function FullPageLoader() {
  return (
    <div
      data-testid="auth-loading"
      className="min-h-screen flex items-center justify-center bg-[#050505] text-white/60 text-sm"
    >
      불러오는 중...
    </div>
  );
}

function RequireAuth({ children, role }) {
  const { isAuthenticated, isAdmin, isCustomer, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader />;

  if (!isAuthenticated) {
    const to = role === "customer" ? "/login" : "/admin/login";
    return <Navigate to={to} replace state={{ from: location }} />;
  }

  if (role === "customer" && !isCustomer) {
    return <Navigate to="/admin" replace />;
  }
  if (role === "admin" && !isAdmin) {
    return <Navigate to="/mypage" replace />;
  }
  return children;
}

function RedirectIfAuthed({ children, expect }) {
  // expect: 'customer' | 'admin' — used on /login, /signup, /admin/login pages
  const { isAuthenticated, isAdmin, isCustomer, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (isAuthenticated) {
    if (isAdmin) return <Navigate to="/admin" replace />;
    if (isCustomer) return <Navigate to="/mypage" replace />;
  }
  return children;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}

const PreviewRoutes = DESIGN_PREVIEW_ENABLED
  ? require("@/pages/designPreview/PreviewRoutes").default // eslint-disable-line global-require
  : null;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/select/:token" element={<CustomerSelectPage />} />
      <Route path="/about" element={<AboutPage2 />} />
      <Route path="/guides" element={<GuidesHubPage />} />
      <Route path="/guides/:slug" element={<GuideDetailPage />} />
      <Route path="/services" element={<ServicesIndexPage />} />
      <Route path="/services/:slug" element={<ServiceLandingPage />} />
      <Route path="/capabilities" element={<CapabilitiesPage />} />
      {PreviewRoutes ? <Route path="/design-preview/*" element={<PreviewRoutes />} /> : null}
      <Route path="/about-2" element={<Navigate to="/about" replace />} />
      <Route path="/portfolio" element={<PortfolioHubPage />} />
      <Route path="/portfolio/:slug/demo" element={<PortfolioDemoPage />} />
      <Route path="/portfolio/:slug" element={<PortfolioDetailPage />} />
      <Route
        path="/login"
        element={
          <RedirectIfAuthed expect="customer">
            <CustomerLogin />
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/signup"
        element={
          <RedirectIfAuthed expect="customer">
            <CustomerSignup />
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/mypage"
        element={
          <RequireAuth role="customer">
            <CustomerMyPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/login"
        element={
          <RedirectIfAuthed expect="admin">
            <AdminLogin />
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAuth role="admin">
            <AdminDashboard />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dsite-hydrated");
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
          <ScrollToTop />
          <CapabilitiesReturnTracker />
          <ScrollProgressGate />
          <PwaInstallPrompt />
          <ThemeProvider>
            <AuthProvider>
              <CmsProvider>
                <SiteKnowledgeProvider>
                  <ContactInquiryProvider>
                    <SeoDocumentHead />
                    <AppRoutes />
                  </ContactInquiryProvider>
                </SiteKnowledgeProvider>
              </CmsProvider>
            </AuthProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}

export default App;
